import * as React from "react";

import HelpIcon from "@mui/icons-material/Help";
import ReportIcon from "@mui/icons-material/Report";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { formatDistance } from "date-fns";

import type { ProximityDetectionConfig } from "@volley/shared/vision-models";

import fetchApi, {
    logFetchError,
    pairedFetchApi,
} from "../../../util/fetchApi";
import CellularStatusIcon from "../../common/CellularStatusIcon";
import WifiStatusIcon from "../../common/WifiStatusIcon";
import { useCurrentUser } from "../../hooks/currentUser";
import { usePairingContext } from "../../hooks/pairingStatus";
import { useStatus } from "../../hooks/status";
import useIntercom from "../../hooks/useIntercom";
import useDialog from "../useDialog";

import AboutListItem from "./AboutListItem";
import BatteryListItem, { BatteryIcon } from "./BatteryListItem";
import HeadUnitListItem from "./HeadUnitListItem";
import ObstructionListItem from "./ObstructionListItem";
import SportPickerListItem from "./SportPickerListItem";
import VolumeListItem from "./VolumeListItem";

export default function DeviceDialog(): React.JSX.Element {
    const intercom = useIntercom();
    const { setDialogType, setDrawerHeight } = useDialog();
    const { status, networkStateCode } = useStatus();
    const { status: pairingStatus, trainerId } = usePairingContext();
    const { isAdmin } = useCurrentUser();
    const [configRequested, setConfigRequested] = React.useState(false);
    const [proximityEnabled, setProximityEnabled] =
        React.useState<boolean>(false);
    const [previousSessionUser, setPreviousSessionUser] = React.useState<
        string | null
    >(null);

    // Set drawer to auto height for V2 dialog
    React.useEffect(() => {
        setDrawerHeight("auto");
    }, [setDrawerHeight]);

    React.useEffect(() => {
        async function fetchProximityConfig() {
            const config = await pairedFetchApi<ProximityDetectionConfig>(
                status?.clientId,
                "/api/proximity/config",
            );
            return config.enabled;
        }

        if (!configRequested && isAdmin() && status?.clientId) {
            setConfigRequested(true);
            fetchProximityConfig()
                .then((enabled) => setProximityEnabled(enabled))
                .catch((e) =>
                    logFetchError(
                        e,
                        "Error loading proximity config status for DeviceDialog",
                    ),
                );
        }
    }, [status?.clientId, configRequested, isAdmin]);

    React.useEffect(() => {
        async function fetchPreviousUser(clientId: number) {
            return await fetchApi<{ username: string; date: string }>(
                `/api/sessions/previous/trainer/${clientId}`,
            );
        }
        if (status?.clientId) {
            fetchPreviousUser(status.clientId)
                .then((username) => {
                    const date = new Date(username.date);
                    const ago = formatDistance(date, new Date(), {
                        addSuffix: true,
                    });
                    setPreviousSessionUser(`${username.username} (${ago})`);
                })
                .catch((e) => logFetchError(e));
        }
    }, [status?.clientId]);

    const calibrated = status?.trainer.calibrated ?? "Uncalibrated";
    const requiresCalibration =
        calibrated !== "Calibrated" || status?.ready === "INIT";
    const isConnecting =
        pairingStatus === "unknown" ||
        (pairingStatus === "paired" && networkStateCode === "Connecting");

    return (
        <Box
            component="div"
            pt={{ sm: 8, xs: 6 }}
            ml={{ sm: "240px" }}
            width={{ sm: "calc(100% - 240px)" }}
        >
            <Stack
                sx={{ p: 2, bgcolor: (t) => t.palette.grey[200] }}
                spacing={2}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <BatteryIcon level={status?.trainer.battery.level} />
                        <WifiStatusIcon
                            wifiStatus={status?.system.wifi ?? null}
                        />
                        <CellularStatusIcon
                            cellularStatus={status?.system.cellular ?? null}
                        />
                    </Stack>
                    <Button
                        variant="contained"
                        color="info"
                        size="small"
                        onClick={() => setDialogType("Finished")}
                    >
                        End Session
                    </Button>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h3" pl={1}>
                        {`Trainer ${trainerId ?? ""}`}
                    </Typography>
                    {isConnecting && <CircularProgress size={24} />}
                </Stack>

                <List
                    sx={{
                        my: 0.5,
                        "& .MuiListItem-root": {
                            borderStyle: "solid",
                            borderWidth: 1,
                            borderColor: (t) => t.palette.grey[400],
                            borderBottomStyle: "none",
                            "&:last-child": {
                                borderBottomStyle: "solid",
                            },
                        },
                    }}
                >
                    {/* Sport Picker */}
                    <SportPickerListItem />

                    {/* Head Unit */}
                    <HeadUnitListItem />

                    {/* Update Available - only shows when update available */}
                    <AboutListItem />

                    {/* Volume */}
                    <VolumeListItem />

                    {/* Battery - only shows when low */}
                    <BatteryListItem />

                    {/* Obstructions - admin only */}
                    {!requiresCalibration && isAdmin() && proximityEnabled && (
                        <ObstructionListItem />
                    )}
                </List>
                <Stack direction="row" justifyContent="space-evenly">
                    <Button
                        onClick={() => {
                            intercom.newMessage();
                        }}
                        startIcon={<HelpIcon />}
                    >
                        Help
                    </Button>
                    <Button
                        onClick={() => setDialogType("ReportIssue")}
                        startIcon={<ReportIcon />}
                    >
                        Report an Issue
                    </Button>
                </Stack>
                {previousSessionUser && (
                    <Typography variant="caption" textAlign="center">
                        Previous session: {previousSessionUser}
                    </Typography>
                )}
                <Button
                    onClick={() => setDialogType("AdvancedSettings")}
                    fullWidth
                    sx={{ mt: 1 }}
                >
                    Control Center
                </Button>
            </Stack>
        </Box>
    );
}
